import * as React from 'react';
import { ReactNode, useEffect } from 'react';
import * as Classes from '../common/classes';
import { SubmissionErrors, ValidationErrors } from 'final-form';
import { useNastaveni } from '@mc/react-app-base';
import { AreaKey, getAreaKey } from '../rows-data/types';
import { IProps } from '../common/props';
import classNames from 'classnames';
import { WizardSteps, WizardStep } from './WizardSteps';
import { Icon } from '../icon/Icon';
import {
  mcGuideCurrentStep,
  mcGuideErrorStep,
  mcGuideFinalStep,
  mcGuideNextStep,
  mcNavigateNext,
  mcNavigatePrev,
} from '@mc/react-ui-icons';
import arrayMutators from 'final-form-arrays';
import { Form, FormRenderProps } from 'react-final-form';
import {
  isErrorShown,
  setErrorsOnly,
  setLastSubmittedValues,
  setSubmitErrorsOnly,
} from '../form/utils';
import { Layout } from '../layout/Layout';
import { Button } from '../button/Button';
import { Spinner } from '../spinner/Spinner';
import { ApiError, getLoadDataError } from '@mc/axios-api';
import { AppError } from '../error/AppError';
import {
  showApiError,
  showUnknownApiError,
  showValidationApiError,
} from '../modal/modalFunctions';

export type WizardChangeStepOptions = {
  noSave?: boolean;
  resetNextProgress?: boolean;
};

export type WizardCloseOptions = {
  noSave?: boolean;
};

export type WizardRenderProps<DATA> = {
  nextButton: () => ReactNode;
  prevButton: () => ReactNode;
  currentStep: string;
  firstRender: boolean;
  finishButton: (props?: { title: string }) => ReactNode;
  saveAndCloseButton: (props?: { title: string }) => ReactNode;
  onSave: () => Promise<boolean>;
  onRefetch: () => Promise<boolean>;
  disabled?: boolean;
  onChangeStep: (
    code: string,
    options?: WizardChangeStepOptions
  ) => Promise<boolean>;
  onClose: (options?: WizardCloseOptions) => Promise<boolean>;
  formRenderProps: FormRenderProps<DATA>;
};

export type WizardHeaderFooterRenderProps = WizardRenderProps<any> & {};

export type WizardStepRenderProps<DATA> = WizardRenderProps<DATA> & {};

export type StepSaveDataOptions = {
  // Pokud je true, tak ukládá data před přechodem na další krok
  nextStep: boolean;
};

export type WizardStep<DATA> = {
  code: string;
  title: ReactNode;
  readData?: () => Promise<Partial<DATA>>;
  /**
   * Uložení dat kroku.
   * @param values data k uložení
   * @param nextStep pokud je true, tak ukládá data před přechodem na další krok
   */
  saveData?: (
    values: DATA,
    options: StepSaveDataOptions
  ) => Promise<SubmissionErrors | undefined | void>;
  fullValidate?: (values: DATA) => ValidationErrors | Promise<ValidationErrors>;
  minimalValidate?: (
    values?: DATA
  ) => ValidationErrors | Promise<ValidationErrors>;
  component: (props: WizardStepRenderProps<DATA>) => ReactNode;
};

export interface IWizardForm extends IProps {
  formKey: AreaKey;
  initialStep?: string;
  steps: WizardStep<any>[];
  footer?: ReactNode | ((props: WizardHeaderFooterRenderProps) => ReactNode);
  header?: ReactNode | ((props: WizardHeaderFooterRenderProps) => ReactNode);
  onClose?: () => void;
}

export type WizardFormProps = IWizardForm;

/**
 * POZOR, je definováno v @mc/react-app-base.
 */
export const GLOBAL_SETTINGS_WIZARD_FORM_KEY = 'globalSettings.wizard.settings';

/**
 * POZOR, je definováno v @mc/react-app-base.
 */
type WizardSettings = {
  currentStep: string;
  lastVisitedStep?: string;
  lastFirstRenderStep?: string;
};

enum DataState {
  LOADING,
  WAITING,
  VIEW,
}

export const WizardForm: React.FC<WizardFormProps> = ({
  formKey,
  className,
  initialStep,
  steps,
  header,
  footer,
  onClose,
}) => {
  if (steps.length === 0) {
    throw new Error('Wizard must have at least one step');
  }

  const [dataState, setDataState] = React.useState<DataState>(
    DataState.LOADING
  );
  const initialStepDataRef = React.useRef<any>(undefined);

  const [firstLoadRetry, setFirstLoadRetry] = React.useState<number>(0);
  const [firstLoadApiError, setFirstLoadApiError] =
    React.useState<any>(undefined);

  const key = getAreaKey(formKey);
  const [wizardSettings, setWizardSettings] = useNastaveni<WizardSettings>(
    `${GLOBAL_SETTINGS_WIZARD_FORM_KEY}.${key}`,
    {
      currentStep: initialStep ?? steps[0].code,
      lastVisitedStep: undefined,
      lastFirstRenderStep: undefined,
    }
  );

  // Current step
  const currentStep =
    steps.find((step) => step.code === wizardSettings.currentStep) || steps[0];
  const currentStepIndex = steps.findIndex(
    (step) => step.code === wizardSettings.currentStep
  );

  // Last visited step
  let lastVisitedStepIndex = steps.findIndex(
    (step) => step.code === wizardSettings.lastVisitedStep
  );
  if (lastVisitedStepIndex === -1) {
    lastVisitedStepIndex = 0;
  }

  // Last firstRender step, pokud se nenajde, je -1, což je OK
  let lastFirstRenderStepIndex = steps.findIndex(
    (step) => step.code === wizardSettings.lastFirstRenderStep
  );

  /**
   *
   * @param stepForSave
   * @param stepForRead
   * @param formRenderProps
   * @return true pokud se vše provedlo v pořádku, false pokud došlo k chybě
   */
  const doSaveAndReadData = async (
    stepForSave: WizardStep<any>,
    stepForRead: WizardStep<any>,
    formRenderProps: FormRenderProps<any>
  ): Promise<boolean> => {
    try {
      setDataState(DataState.WAITING);

      // Save current step data
      if (stepForSave.saveData) {
        try {
          // Pokud step for read je po step for save, jde na další krok
          const nextStepIndex = steps.findIndex(
            (step) => step.code === stepForRead.code
          );
          const isNextStep = nextStepIndex > currentStepIndex;
          const result = await stepForSave.saveData(
            formRenderProps.values || {},
            {
              nextStep: isNextStep,
            }
          );
          if (result && Object.keys(result).length > 0) {
            formRenderProps.form.mutators.setSubmitErrorsOnly(result);
            formRenderProps.form.mutators.setLastSubmittedValues(
              formRenderProps.values || {}
            );
            return false;
          }
        } catch (err) {
          return false;
        }
      }

      // Read next step data
      if (stepForRead.readData) {
        try {
          // @ts-ignore
          const newData = await readDataCall(() => stepForRead.readData());
          initialStepDataRef.current = newData;
          return true;
        } catch (err) {
          // Chybu načtení NEbudeme nastavovat, spolehneme že se např. jen nepřejde na další krok, neprovede refresh apod. - to vyřeší actionApiCall vně wizardu
          // setFirstLoadApiError(err);
          return false;
        }
      } else {
        initialStepDataRef.current = undefined;
        return true;
      }
    } catch (err) {
      return false;
    } finally {
      setDataState(DataState.VIEW);
    }
  };

  const doReadData = async (
    step: WizardStep<any>,
    state: DataState
  ): Promise<boolean> => {
    if (step.readData) {
      setDataState(state);
      try {
        let data;
        if (state === DataState.LOADING) {
          // Při prvním načtení chybu zobrazujeme uvnitř wizardu
          data = await step.readData();
        } else {
          // Při refresh zobrazujeme chybu jen v error dialogu (protože necháme načtená předchozí data)
          // @ts-ignore
          data = await readDataCall(() => step.readData());
        }
        initialStepDataRef.current = data;
        setFirstLoadApiError(undefined);
        return true;
      } catch (err) {
        // Chybu načtení budeme nastavovat jen při prvním načtení, jinak se spolehneme že se např. jen nepřejde na další krok, neprovede refresh apod. - to vyřeší actionApiCall vně wizardu
        if (state === DataState.LOADING) {
          setFirstLoadApiError(err);
        }
        return false;
      } finally {
        setDataState(DataState.VIEW);
      }
    } else {
      setDataState(DataState.VIEW);
      return true;
    }
  };

  const getFirstRenderStepCode = (currentStepCode: string) => {
    const newStepIndex = steps.findIndex(
      (step) => step.code === currentStepCode
    );
    if (newStepIndex > lastFirstRenderStepIndex) {
      return currentStepCode;
    } else {
      return wizardSettings.lastFirstRenderStep;
    }
  };

  const doSaveData = async (
    step: WizardStep<any>,
    formRenderProps: FormRenderProps<any>,
    nextStep: boolean
  ): Promise<boolean> => {
    if (step.saveData) {
      setDataState(DataState.WAITING);
      try {
        const result = await step.saveData(formRenderProps.values || {}, {
          nextStep: nextStep,
        });
        if (result && Object.keys(result).length > 0) {
          formRenderProps.form.mutators.setSubmitErrorsOnly(result);
          formRenderProps.form.mutators.setLastSubmittedValues(
            formRenderProps.values || {}
          );
          return false;
        }
      } catch (err) {
        return false;
      } finally {
        setDataState(DataState.VIEW);
      }
    }

    setWizardSettings({
      ...wizardSettings,
      lastFirstRenderStep: getFirstRenderStepCode(step.code),
    });

    return true;
  };

  useEffect(() => {
    doReadData(currentStep, DataState.LOADING);
  }, [firstLoadRetry]);

  /**
   * Změna kroku průvodce - v sekci seznamu kroků.
   * @param newStep
   */
  const handleWizardStepChange = async (
    formRenderProps: FormRenderProps<any>,
    newStep: WizardStep<any>
  ): Promise<boolean> => {
    const nextStepIndex = steps.findIndex((step) => step.code === newStep.code);
    const isNextStep = nextStepIndex > currentStepIndex;

    // Uložení stávajícíh dat a načtení nových dat
    const validate = isNextStep ? fullValidate : minimalValidate;
    if (await validate(formRenderProps)) {
      if (await doSaveAndReadData(currentStep, newStep, formRenderProps)) {
        // Změna kroku
        const newSettings: WizardSettings = {
          ...wizardSettings,
          currentStep: newStep.code,
        };

        if (lastVisitedStepIndex < nextStepIndex) {
          newSettings.lastVisitedStep = newStep.code;
        }

        newSettings.lastFirstRenderStep = getFirstRenderStepCode(
          currentStep.code
        );

        setWizardSettings(newSettings);
        return true;
      } else {
        return false;
      }
    } else {
      return false;
    }
  };

  const minimalValidate = async (formRenderProps: FormRenderProps<any>) => {
    if (currentStep.minimalValidate) {
      const errors = await currentStep.minimalValidate(formRenderProps.values);
      formRenderProps.form.mutators.setErrorsOnly(errors);
      if (errors && Object.keys(errors).length > 0) {
        return false;
      } else {
        return true;
      }
    } else {
      return true;
    }
  };

  const fullValidate = async (formRenderProps: FormRenderProps<any>) => {
    if (currentStep.fullValidate) {
      const errors = await currentStep.fullValidate(formRenderProps.values);
      formRenderProps.form.mutators.setErrorsOnly(errors);
      if (errors && Object.keys(errors).length > 0) {
        return false;
      } else {
        return true;
      }
    } else {
      return true;
    }
  };

  const handleClose = async (
    formRenderProps: FormRenderProps<any>,
    options?: WizardCloseOptions
  ): Promise<boolean> => {
    const needSave = !options?.noSave;
    const validate: (
      formRenderProps: FormRenderProps<any>
    ) => Promise<boolean> = needSave ? minimalValidate : async () => true;

    if (await validate(formRenderProps)) {
      if (needSave) {
        if (!(await doSaveData(currentStep, formRenderProps, false))) {
          return false;
        }
      }
      onClose?.();
      return true;
    } else {
      return false;
    }
  };

  const handleFinish = async (formRenderProps: FormRenderProps<any>) => {
    if (await fullValidate(formRenderProps)) {
      if (await doSaveData(currentStep, formRenderProps, true)) {
        return true;
      } else {
        return false;
      }
    } else {
      return false;
    }
  };

  const handleSave = async (formRenderProps: FormRenderProps<any>) => {
    if (await minimalValidate(formRenderProps)) {
      if (await doSaveData(currentStep, formRenderProps, false)) {
        return true;
      } else {
        return false;
      }
    } else {
      return false;
    }
  };

  const handleRefetch = async (
    // @ts-ignore
    formRenderProps: FormRenderProps<any>
  ) => {
    return await doReadData(currentStep, DataState.WAITING);
  };

  const handleChangeStep = async (
    formRenderProps: FormRenderProps<any>,
    newStepCode: string,
    options?: WizardChangeStepOptions
  ) => {
    const newStep = steps.find((step) => step.code === newStepCode);
    if (!newStep) {
      throw new Error(`Step with code ${newStepCode} not found`);
    }
    const nextStepIndex = steps.findIndex((step) => step.code === newStep.code);
    if (nextStepIndex > lastVisitedStepIndex + 1) {
      return false;
    }

    const isNextStep = nextStepIndex > currentStepIndex;

    // Uložení stávajícíh dat a načtení nových dat
    const needSave = !options?.noSave;
    const validate: (
      formRenderProps: FormRenderProps<any>
    ) => Promise<boolean> = needSave
      ? isNextStep
        ? fullValidate
        : minimalValidate
      : async () => true;

    if (await validate(formRenderProps)) {
      if (await doSaveAndReadData(currentStep, newStep, formRenderProps)) {
        // Změna kroku
        const newSettings: WizardSettings = {
          ...wizardSettings,
          currentStep: newStep.code,
        };

        if (
          lastVisitedStepIndex < nextStepIndex ||
          options?.resetNextProgress
        ) {
          newSettings.lastVisitedStep = newStep.code;
        }

        if (
          options?.resetNextProgress &&
          lastFirstRenderStepIndex >= nextStepIndex
        ) {
          newSettings.lastFirstRenderStep =
            nextStepIndex - 1 < 0 ? undefined : steps[nextStepIndex - 1].code;
        } else {
          newSettings.lastFirstRenderStep = getFirstRenderStepCode(
            currentStep.code
          );
        }

        setWizardSettings(newSettings);

        return true;
      } else {
        return false;
      }
    } else {
      return false;
    }
  };

  if (dataState === DataState.LOADING) {
    return (
      <div className={classNames(className, Classes.WIZARD_FORM)}>
        <Spinner className={'h-100'} />
        <div className={Classes.WIZARD_FORM_STEPS}></div>
        <div className={Classes.WIZARD_FORM_CONTENT}></div>
      </div>
    );
  }

  return (
    <Form
      key={`${currentStep.code}`}
      initialValues={initialStepDataRef.current}
      onSubmit={() => {}}
      validate={currentStep.fullValidate}
      mutators={{
        ...arrayMutators,
        setErrorsOnly,
        setSubmitErrorsOnly,
        setLastSubmittedValues,
      }}
    >
      {(formRenderProps) => {
        const renderProps: WizardRenderProps<any> = {
          currentStep: currentStep.code,
          firstRender: currentStepIndex > lastFirstRenderStepIndex,
          onClose: async (options) => handleClose(formRenderProps, options),
          onSave: async () => handleSave(formRenderProps),
          onRefetch: async () => handleRefetch(formRenderProps),
          formRenderProps: formRenderProps,
          onChangeStep: async (code, options) =>
            handleChangeStep(formRenderProps, code, options),
          disabled: dataState === DataState.WAITING,
          finishButton: (props) =>
            currentStepIndex === steps.length - 1 ? (
              <Button
                style={'primary'}
                onClick={async () => {
                  await handleFinish(formRenderProps);
                }}
              >
                {props?.title ?? 'Dokončit'}
              </Button>
            ) : null,
          saveAndCloseButton: (props) => (
            <Button
              onClick={async () => {
                await handleClose(formRenderProps);
              }}
            >
              {props?.title ?? 'Uložit a zavřít'}
            </Button>
          ),
          prevButton: () => {
            if (currentStepIndex === 0) {
              return null;
            } else {
              return (
                <Button
                  onClick={() =>
                    handleWizardStepChange(
                      formRenderProps,
                      steps[currentStepIndex - 1]
                    )
                  }
                  icon={<Icon fixedWidth icon={mcNavigatePrev} />}
                >
                  {steps[currentStepIndex - 1].title}
                </Button>
              );
            }
          },
          nextButton: () => {
            if (currentStepIndex + 1 === steps.length) {
              return null;
            } else {
              return (
                <Button
                  style={'primary'}
                  onClick={() =>
                    handleWizardStepChange(
                      formRenderProps,
                      steps[currentStepIndex + 1]
                    )
                  }
                  icon={<Icon fixedWidth icon={mcNavigateNext} />}
                >
                  {steps[currentStepIndex + 1].title}
                </Button>
              );
            }
          },
        };
        const headerFooterRenderProps: WizardHeaderFooterRenderProps = {
          ...renderProps,
        };
        const currentStepRenderProps: WizardStepRenderProps<any> = {
          ...renderProps,
        };

        if (firstLoadApiError) {
          const error = getLoadDataError(firstLoadApiError);
          return (
            <Layout
              mode={'viewDetail'}
              disableScroll
              h100
              contentClassName={'d-flex align-items-center'}
            >
              <AppError message={'Data se nepodařilo načíst'}>
                {error.message || 'Data se nepodařilo načíst'}
                <Button
                  className={'mt-2'}
                  cssStyle={{
                    justifySelf: 'center',
                  }}
                  style={'primary'}
                  onClick={() => setFirstLoadRetry(firstLoadRetry + 1)}
                >
                  Opakovat
                </Button>
              </AppError>
            </Layout>
          );
        }

        return (
          <Layout
            mode={'viewDetail'}
            disableScroll
            h100
            header={
              typeof header === 'function'
                ? header(headerFooterRenderProps)
                : header
            }
          >
            <div className={classNames(className, Classes.WIZARD_FORM)}>
              <div className={Classes.WIZARD_FORM_STEPS}>
                <WizardSteps>
                  {steps.map((step, index) => {
                    const title = step.title;
                    const showError =
                      step.code === currentStep.code &&
                      isErrorShown(
                        formRenderProps.errors,
                        formRenderProps.touched
                      );

                    let icon: any;
                    if (showError) {
                      icon = mcGuideErrorStep;
                    } else if (index === lastVisitedStepIndex) {
                      icon = mcGuideCurrentStep;
                    } else if (index < lastVisitedStepIndex) {
                      icon = mcGuideFinalStep;
                    } else {
                      icon = mcGuideNextStep;
                    }

                    return (
                      <WizardStep
                        active={step.code === currentStep.code}
                        visited={index <= lastVisitedStepIndex}
                        error={showError}
                        last={index === steps.length - 1}
                        // error={'najaka chyba'}
                        disabled={
                          index > lastVisitedStepIndex &&
                          currentStepIndex + 1 !== index
                        }
                        onClick={
                          dataState === DataState.WAITING
                            ? undefined
                            : () =>
                                handleWizardStepChange(formRenderProps, step)
                        }
                      >
                        <Icon fixedWidth icon={icon} className={'mr-2'} />
                        {title}
                      </WizardStep>
                    );
                  })}
                </WizardSteps>
              </div>
              <div className={Classes.WIZARD_FORM_CONTENT}>
                <Layout
                  h100
                  footerClassName={'p-2'}
                  contentClassName={'p-2'}
                  footer={
                    typeof footer === 'function'
                      ? footer(headerFooterRenderProps)
                      : footer
                  }
                >
                  {currentStep.component(currentStepRenderProps)}
                </Layout>
              </div>
              {dataState === DataState.WAITING && (
                <Spinner className={'h-100'} />
              )}
            </div>
          </Layout>
        );
      }}
    </Form>
  );
};

function readDataCall<T>(process: () => Promise<T>): Promise<T> {
  return new Promise<T>((resolve, reject) => {
    process()
      .then((result) => {
        resolve(result);
      })
      .catch((_error) => {
        if (_error.stack && _error.message) {
          console.error(_error);

          showUnknownApiError();
          reject(_error);
        } else {
          const error = _error as ApiError;

          // Request byl programově přerušen
          if (error.cancelled) {
            reject(_error);
          } else {
            switch (error.status) {
              case 422:
                showValidationApiError(error);
                reject(error);
                break;
              default:
                showApiError(error);
                reject(error);
                break;
            }
          }
        }
      });
  });
}
